import React, { useCallback } from "react";

import { add, isValid, set } from "date-fns";

import { DevTool } from "@hookform/devtools";

import { DatePicker } from "@/components/date-picker/DatePicker";
import { Button } from "@/components/form/Button/Button";
import { TextField } from "@/components/form/text-field/TextField";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { TimePicker } from "@/components/time-picker/TimePicker";
import { useLadyProfileDto } from "@/features/lady-profile/hooks";
import { useCreatePhoneCall } from "@/features/phone-call-create/hooks/useCreatePhoneCall";
import { useFormFactory } from "@/hooks/useFormFactory";

import { mapFormToQuery, useFormattedTime } from "../helpers";
import { CreatePhoneCallFormSchema } from "../models/CreatePhoneCallForm.schema";

type PropTypes = {
  ladyId: number;
};

export const CreatePhoneCallForm: React.FC<PropTypes> = ({ ladyId }) => {
  const { data: ladyDto } = useLadyProfileDto();
  const { mutate: createPhoneCall } = useCreatePhoneCall();
  const currentTimeAndZodeString = useFormattedTime(new Date());

  const { removeOne } = useOpenModal();

  const createPhoneCallWithMap = useCallback(
    (formData: CreatePhoneCallFormSchema) => {
      createPhoneCall(mapFormToQuery(formData, { ladyId }));
    },
    [createPhoneCall, ladyId],
  );

  const {
    register,
    onSubmitHandler,
    control,
    watch,
    getFieldState,
    formState: { errors },
  } = useFormFactory({
    schema: CreatePhoneCallFormSchema,
    mutate: (formData) => {
      createPhoneCallWithMap(formData);
    },
  });

  const { openModal } = useOpenModal();
  const openErrorModal = useCallback(
    (message: string) =>
      openModal("INFORMATION_MODAL", {
        statusReg: "fail",
        message,
      }),
    [openModal],
  );

  const handleWithErrorSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      await onSubmitHandler(e);

      const phoneErrorMessage = getFieldState("phone")?.error?.message;

      if (phoneErrorMessage) {
        openErrorModal(phoneErrorMessage);
        return;
      }

      const timeErrorMessage = getFieldState("time")?.error?.message;

      if (timeErrorMessage) {
        openErrorModal(timeErrorMessage);
        return;
      }
    },
    [getFieldState, onSubmitHandler, openErrorModal],
  );

  const callDate: Date | null = watch("date") || null;
  const callTime: Date | null = watch("time") || null;

  const displayCallTime =
    (callDate &&
      callTime &&
      `${callDate.getDate().toString().padStart(2, "0")}.${(
        callDate.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}.${callDate.getFullYear()} ${callTime
        .getHours()
        .toString()
        .padStart(2, "0")}:${callTime
        .getMinutes()
        .toString()
        .padStart(2, "0")}`) ||
    null;

  return (
    <form className="flex flex-col gap-4" onSubmit={handleWithErrorSubmit}>
      <h2 className="text-[22px] lg:text-2xl text-grey1-800 font-[680] text-center">
        Phone call reservation
      </h2>

      <p className="text-sm">
        The reservation is placed at least 24 hours prior to the call
      </p>

      <p className="text-sm">
        Your current time and zone: {currentTimeAndZodeString}
      </p>

      <DatePicker
        control={control}
        name="date"
        label="Date"
        minDate={add(new Date(), { hours: 24 })}
        maxDate={add(new Date(), { years: 1 })}
        defaultValue={add(new Date(), { days: 1 })}
      />

      <TimePicker
        control={control}
        name="time"
        label="Time"
        defaultValue={set(add(new Date(), { hours: 1 }), { minutes: 0 })}
        slotProps={{
          textField: { error: Boolean(errors.time) },
        }}
        timeSteps={{ minutes: 15 }}
      />

      <p className="text-sm">
        Please be aware of that lady&apos;s local time for phone call should be
        between 11:00 and 22:00.
      </p>

      <TextField
        error={Boolean(errors.phone)}
        {...register("phone")}
        label="Your Phone"
      ></TextField>
      {isValid(callDate) && isValid(callTime) && (
        <p className="text-sm">
          {` You book a telephone conversation with ${
            ladyDto?.forms.name || "lady"
          } (ID: ${ladyId})${
            !!displayCallTime
              ? `, which will be started on ${displayCallTime} `
              : ""
          }`}
        </p>
      )}

      <div className="flex justify-between gap-4 items-end">
        <Button
          variant="text"
          onHoverVariant="color-reverse"
          className="xxs:text-sm sm:text-base h-[45px] rounded-full text-grey1-900"
          onClick={removeOne}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          onHoverVariant="color-reverse"
          className="xxs:text-sm sm:text-base h-[45px] rounded-full"
        >
          Book a call
        </Button>
        <DevTool control={control} />
      </div>
    </form>
  );
};
